import { useState } from 'react';
import { AlertTriangle, ArrowLeft, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '@utils/api/users/use-get-organization';
import { TokenExtension } from '@utils/extensions/token.extension';
import { getHomeOrg } from '@utils/extensions/home-org.extension';

/* eslint-disable no-restricted-syntax -- Semantic impersonation warning colors (amber) must stay stable across themes */
const BANNER_STYLE = {
  backgroundColor: '#fffbeb',
  borderColor: '#fcd34d',
  color: '#92400e'
};
/* eslint-enable no-restricted-syntax */

export interface ActingAsBannerProps {
  // Only show the banner when this returns true. Each surface gates by its
  // own role (SYSOP, reseller) so the banner stays scoped.
  enabled: boolean;
  // localStorage prefix the home-org snapshot was saved under.
  homeOrgKeyPrefix: string;
  // Where the "back" link sends the user.
  backLink: string;
  // i18n keys with sensible defaults.
  bannerMessageKey?: string;
  backLinkKey?: string;
}

export function ActingAsBanner({
  enabled,
  homeOrgKeyPrefix,
  backLink,
  bannerMessageKey = 'acting-as-banner',
  backLinkKey = 'back-to-backoffice'
}: ActingAsBannerProps) {
  const { t } = useTranslation();
  const { data: organization } = useOrganization();
  const [collapsed, setCollapsed] = useState(false);

  const token = TokenExtension.get();
  const uid = token?.decoded?.uid;
  const currentOid = token?.decoded?.oid?.toString() ?? '';

  if (!enabled || !uid) return null;

  const home = getHomeOrg(homeOrgKeyPrefix, uid);
  if (!home || home.orgId === currentOid) return null;

  // Collapsed: a small floating pill that reveals content behind the banner
  // while keeping the impersonation context one click away.
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        aria-label={t(bannerMessageKey, { orgName: organization?.name ?? '…' })}
        className="fixed bottom-4 right-4 z-50 flex h-10 w-10 items-center justify-center rounded-full shadow-pop border hover:opacity-90 transition-opacity"
        style={BANNER_STYLE}
      >
        <AlertTriangle className="h-4 w-4" />
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-4 right-4 z-50 max-w-xs w-full rounded-2xl shadow-pop border flex flex-col gap-3 p-4"
      style={BANNER_STYLE}
    >
      <button
        type="button"
        onClick={() => setCollapsed(true)}
        aria-label={t('dismiss', 'Dismiss')}
        className="absolute top-2 right-2 inline-flex items-center justify-center rounded-md p-1 hover:opacity-75 transition-opacity"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <div className="flex items-start gap-2 pr-5">
        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <span className="text-sm font-medium leading-snug">
          {t(bannerMessageKey, { orgName: organization?.name ?? '…' })}
        </span>
      </div>
      <a
        href={backLink}
        className="inline-flex items-center gap-1.5 text-sm font-semibold underline hover:opacity-75 transition-opacity"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        {t(backLinkKey)}
      </a>
    </div>
  );
}
